import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CepStateService, FlashMessageService } from '@libs/shared/common';
import { CatalogSearchResultModel} from '@libs/shop/models';
import { ScannerHandlerControlsModel, ScannerHeaderConfig, ScannerScanResult } from '@libs/shared/models';
import { defer, from, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CartService } from '../cart/cart.service';
import { CatalogSearchRepositoryService } from '../catalog-search';
import { ScannerHandlerBase } from '../scanner-handler';
import { ScannerHandler } from '../scanner-handler/scanner-handler.decorator';

@Injectable({
  providedIn: 'root',
})
@ScannerHandler()
export class CartScannerHandlerService extends ScannerHandlerBase {
  public static override readonly Key: string = 'CART_SCANNER';

  private readonly cepStateService: CepStateService = inject(CepStateService);
  private readonly searchService: CatalogSearchRepositoryService = inject(CatalogSearchRepositoryService);
  private readonly cartService: CartService = inject(CartService);
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);
  private readonly cartCount: Signal<string> = toSignal(this.cartService.getCartItemsCountView(), { initialValue: '0' });

  public override handleOrderModeEnabledScan(value: string, controls: ScannerHandlerControlsModel): Observable<void> {
    return defer(() => {
      controls.showLoader();

      return this.addToCartIfSingleResultIsFound(value).pipe(switchMap((result) => {
          void controls.hideLoader();

          switch (result) {
            case 'success':
              return controls.showSuccess();
            case 'notFound':
              this.flashMessageService.showMessage('errorScannerProductNotFound');

              return controls.showError();
            case 'multipleFound':
              return this.navigateToSearch(value);
          }
        }),
        catchError((e) => {
          this.flashMessageService.showMessage('errorSearchResult');
          console.error(e);

          return of(undefined);
        }));
    });
  }

  public override handleOrderModeDisabledScan(value: string): Observable<void> {
    return this.navigateToSearch(value);
  }

  public override handleMultiScan(value: string): Observable<void> {
    return this.navigateToSearch(value);
  }

  public override getHeaderConfig(): ScannerHeaderConfig {
    return {
      title: 'Artikel Scannen',
      iconTitle: 'Warenkorb',
      icon: 'cart-shopping',
      iconStyle: 'light',
      link: '/cart',
      badge: this.cartCount,
    };
  }

  private navigateToSearch(searchText: string): Observable<void> {
    return from(this.cepStateService.navigateToSearch(searchText)).pipe(
      map(() => { /* empty */
      }));
  }

  private addToCartIfSingleResultIsFound(code: string): Observable<ScannerScanResult> {
    return this.searchService.search({ q: code }).pipe(map((searchResult: CatalogSearchResultModel) => {
      if (!searchResult.products.length) {
        return 'notFound';
      }

      if (searchResult.products.length > 1) {
        return 'multipleFound';
      }

      this.cartService.queueCartItem('add', {
        sku: searchResult.products[0].sku, quantity: 1,
      });

      return 'success';
    }));
  }
}
